import React, { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import * as adminApi from '../api/adminApi';
import { PageHeader, Card, CardBody, ErrorBanner } from '../components/PageChrome';
import { CommissionOrderFlowSection } from '../components/commission/CommissionOrderFlowSection';
import { pickErrorMessage } from '../lib/apiHelpers';

const BACK_LABELS = {
  '/analytics': 'Back to analytics',
  '/order-history': 'Back to order history',
};

export default function CommissionBreakdown() {
  const { orderKey: routeOrderKey } = useParams();
  const location = useLocation();
  const backTo = location.state?.from === '/analytics' ? '/analytics' : '/order-history';
  const backLabel = BACK_LABELS[backTo] ?? 'Back to order history';

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!routeOrderKey) {
      setOrder(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const key = decodeURIComponent(routeOrderKey);

    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await adminApi.fetchOrderCommissionFlow(key);
        if (cancelled) return;
        setOrder({
          orderKey: res.orderKey ?? key,
          placedAt: res.placedAt ?? location.state?.placedAt ?? null,
          paymentMethod: res.paymentMethod ?? res.flow?.paymentMethod ?? null,
          flow: res.flow,
        });
      } catch (err) {
        if (cancelled) return;
        setError(pickErrorMessage(err));
        setOrder(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [routeOrderKey, location.state?.placedAt]);

  if (!routeOrderKey) {
    return (
      <>
        <PageHeader title="Order commission" subtitle="Per-order distribution flow" />
        <Card>
          <CardBody className="p-4 text-center">
            <p className="text-secondary mb-3">Open an order from Analytics or Order history to view its commission flow.</p>
            <div className="d-flex flex-wrap justify-content-center gap-2">
              <Link to="/analytics" className="btn btn-outline-secondary">
                Analytics
              </Link>
              <Link to="/order-history" className="btn btn-primary">
                Order history
              </Link>
            </div>
          </CardBody>
        </Card>
      </>
    );
  }

  if (loading) {
    return (
      <>
        <PageHeader title="Order commission" subtitle="Per-order distribution flow" />
        <Card>
          <CardBody className="p-4 text-center text-secondary">Loading commission flow…</CardBody>
        </Card>
      </>
    );
  }

  if (error || !order) {
    return (
      <>
        <PageHeader
          title="Order commission"
          subtitle="Per-order distribution flow"
          actions={
            <Link to={backTo} className="btn btn-outline-secondary btn-sm">
              {backLabel}
            </Link>
          }
        />
        <ErrorBanner message={error || 'Order not found'} />
        <Card>
          <CardBody className="p-4 text-center">
            <p className="text-secondary mb-3">Open an order from Analytics or Order history to view its commission flow.</p>
            <div className="d-flex flex-wrap justify-content-center gap-2">
              <Link to="/analytics" className="btn btn-outline-secondary">
                Analytics
              </Link>
              <Link to="/order-history" className="btn btn-primary">
                Order history
              </Link>
            </div>
          </CardBody>
        </Card>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Order commission"
        subtitle="Commission distribution for this order"
        actions={
          <Link to={backTo} className="btn btn-outline-secondary btn-sm">
            {backLabel}
          </Link>
        }
      />

      <CommissionOrderFlowSection
        flow={order.flow}
        orderLabel={order.orderKey}
        placedAt={order.placedAt}
        paymentMethod={order.paymentMethod}
      />
    </>
  );
}
